import React, { useState, useCallback } from 'react';
import {
  ChevronLeft, Check, X, ScanLine, QrCode, Barcode, Package, FileText, Box,
  Truck, User, Flame, Navigation, Building2, ShoppingCart, Receipt,
} from 'lucide-react';
import { getActionDef } from '../../data/immersiveData';
import { neoUi } from './neoUiTheme';
import { NeoPulseTabView } from './NeoPulseOverview';

const A = { primary: '#6366f1', success: '#10b981', danger: '#ef4444' };

export { neoUi } from './neoUiTheme';

const ICON_MAP = {
  cube: Box,
  'document-text': FileText,
  boat: Truck,
  car: Truck,
  flame: Flame,
  'swap-vertical': Package,
  bulb: Package,
  flash: Package,
  thermometer: Package,
  'bar-chart': Package,
  'pie-chart': Package,
  pulse: Package,
  navigate: Navigation,
  snow: Package,
  laptop: Package,
  construct: Package,
  person: User,
  business: Building2,
  receipt: Receipt,
  cart: ShoppingCart,
  'trending-up': Package,
  'alert-circle': Package,
};

/** Lucide icon for list rows (immersive data `item.icon` keys) */
export function NeoImmersiveItemIcon({ name, color, size = 22 }) {
  const Lucide = ICON_MAP[name] || Package;
  return <Lucide size={size} color={color} />;
}

function ItemIcon(props) {
  return <NeoImmersiveItemIcon {...props} />;
}

function StepTimeline({ steps, color, isDark }) {
  if (!steps?.length) return null;
  const line = isDark ? 'bg-white/10' : 'bg-black/10';
  const dim = isDark ? 'text-white/40' : 'text-black/45';
  return (
    <div className="mt-2 space-y-0">
      {steps.map((s, i) => {
        const done = s.status === 'completed';
        const cur = s.status === 'current';
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className="w-3 h-3 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  backgroundColor: done ? A.success : cur ? color : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  border: cur ? `2px solid ${color}` : 'none',
                }}
              >
                {done && <Check size={8} className="text-white" />}
              </div>
              {i < steps.length - 1 && (
                <div className={`w-px flex-1 min-h-[20px] ${line}`} />
              )}
            </div>
            <div className="pb-4">
              <p className={`text-sm font-semibold ${cur ? (isDark ? 'text-white' : 'text-black') : isDark ? 'text-white/70' : 'text-black/65'}`}>{s.step}</p>
              {s.user && <p className={`text-xs ${dim} mt-0.5`}>{s.user}</p>}
              {s.date && <p className={`text-[10px] ${isDark ? 'text-white/35' : 'text-black/40'}`}>{s.date}</p>}
              {s.detail && <p className={`text-xs ${isDark ? 'text-white/50' : 'text-black/50'} mt-1 italic`}>{s.detail}</p>}
              {s.location && <p className={`text-xs ${dim} mt-0.5`}>{s.location}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, children, ui }) {
  return (
    <div className={`rounded-2xl border p-4 mb-3 ${ui.section}`}>
      {title && <p className={`text-[10px] uppercase tracking-widest mb-3 ${ui.sectionTitle}`}>{title}</p>}
      {children}
    </div>
  );
}

function MetricRow({ label, value, ui }) {
  return (
    <div className={`flex justify-between py-2 border-b last:border-0 ${ui.borderSubtle}`}>
      <span className={`text-sm ${ui.muted}`}>{label}</span>
      <span className={`text-sm font-medium text-right max-w-[55%] ${ui.title}`}>{value}</span>
    </div>
  );
}

export function NeoDetailView({ item, color, onBack, listActions, isDark = true }) {
  const ui = neoUi(isDark);
  const hasActions = listActions?.includes?.('approve');

  const handleAction = (type) => {
    window.alert(`${type === 'approve' ? 'Approved' : 'Rejected'}: ${item.title}`);
    onBack();
  };

  return (
    <div className={`flex flex-col h-full min-h-0 ${ui.page}`}>
      <header className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold tracking-wide truncate pr-10 ${ui.title}`}>{item.title}</h2>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-24">
        <div className={`rounded-2xl border p-5 text-center mb-4 ${ui.card}`}>
          <div className="inline-flex p-4 rounded-2xl border mb-3" style={{ borderColor: color, backgroundColor: `${color}22` }}>
            <ItemIcon name={item.icon} color={color} size={32} />
          </div>
          <p className={`text-lg font-bold ${ui.title}`}>{item.title}</p>
          <p className={`text-xs mt-1 ${ui.muted}`}>{item.subtitle}</p>
          <span
            className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: `${item.statusColor}22`, color: item.statusColor }}
          >
            {item.status}
          </span>
        </div>

        {item.workflow && (
          <Section title="Workflow" ui={ui}>
            <StepTimeline steps={item.workflow} color={color} isDark={isDark} />
          </Section>
        )}
        {item.tracking && Array.isArray(item.tracking) && (
          <Section title="Tracking" ui={ui}>
            <StepTimeline steps={item.tracking} color={color} isDark={isDark} />
          </Section>
        )}
        {item.timeline && (
          <Section title="Timeline" ui={ui}>
            <StepTimeline steps={item.timeline} color={color} isDark={isDark} />
          </Section>
        )}
        {item.lineItems && (
          <Section title="Items" ui={ui}>
            {item.lineItems.map((li, i) => (
              <div key={i} className={`flex justify-between py-2 ${ui.borderSubtle}`}>
                <div>
                  <p className={`text-sm font-medium ${ui.title}`}>{li.name}</p>
                  <p className={`text-xs ${ui.muted}`}>Qty: {li.qty} × {li.price}</p>
                </div>
                <p className={`text-sm font-semibold ${ui.title}`}>{li.total}</p>
              </div>
            ))}
            <div className={`flex justify-between pt-3 mt-2 border-t border-opacity-20 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
              <span className={`text-sm font-bold ${ui.muted}`}>Total</span>
              <span className={`text-sm font-bold ${ui.title}`}>{item.detail}</span>
            </div>
          </Section>
        )}
        {item.metrics && (
          <Section title="Metrics" ui={ui}>
            {Object.entries(item.metrics).map(([k, v]) => (
              <MetricRow key={k} label={k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())} value={String(v)} ui={ui} />
            ))}
          </Section>
        )}
        {item.asset && (
          <Section title="Asset" ui={ui}>
            <MetricRow label="Name" value={item.asset.name} ui={ui} />
            <MetricRow label="ID" value={item.asset.id} ui={ui} />
          </Section>
        )}
        {item.sla && (
          <Section title="SLA" ui={ui}>
            <MetricRow label="Response" value={item.sla.response} ui={ui} />
            <MetricRow label="Resolution" value={item.sla.resolution} ui={ui} />
          </Section>
        )}
        <Section title="Reference" ui={ui}>
          <MetricRow label="ID" value={item.id} ui={ui} />
          {item.detail && <MetricRow label="Value" value={item.detail} ui={ui} />}
        </Section>

        {hasActions && (
          <div className="flex gap-3 mt-4">
            {listActions.includes('approve') && (
              <button
                type="button"
                onClick={() => handleAction('approve')}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white"
                style={{ backgroundColor: A.success }}
              >
                <Check size={18} /> Approve
              </button>
            )}
            {listActions.includes('reject') && (
              <button
                type="button"
                onClick={() => handleAction('reject')}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-red-600/90"
              >
                <X size={18} /> Reject
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function NeoFormView({ actionId, color, onBack, onSubmit, isDark = true }) {
  const ui = neoUi(isDark);
  const def = getActionDef(actionId);
  const [formData, setFormData] = useState({});

  const update = useCallback((k, v) => setFormData((p) => ({ ...p, [k]: v })), []);

  const submit = useCallback(() => {
    if (!def?.fields) return;
    const missing = def.fields.some((f) => !String(formData[f.key] ?? '').trim());
    if (missing) {
      window.alert('Incomplete — fill all fields.');
      return;
    }
    onSubmit();
  }, [def, formData, onSubmit]);

  if (!def || def.type !== 'form') return null;

  return (
    <div className={`flex flex-col h-full min-h-0 ${ui.page}`}>
      <header className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold tracking-wide truncate pr-10 ${ui.title}`}>{def.title}</h2>
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {def.fields.map((f) => (
          <div key={f.key}>
            <label className={`text-xs font-semibold uppercase tracking-wide ${ui.muted}`}>{f.label}</label>
            <div className={`mt-2 flex rounded-xl border overflow-hidden ${ui.inputWrap}`}>
              {f.prefix && <span className={`pl-3 py-3 text-sm shrink-0 ${ui.muted}`}>{f.prefix}</span>}
              {f.type === 'textarea' ? (
                <textarea
                  className={`flex-1 min-h-[100px] px-3 py-3 bg-transparent text-sm outline-none resize-y ${ui.input}`}
                  placeholder={f.placeholder}
                  value={formData[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value)}
                />
              ) : (
                <input
                  className={`flex-1 px-3 py-3 bg-transparent text-sm outline-none ${ui.input}`}
                  placeholder={f.placeholder}
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={formData[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={`p-5 pt-2 border-t shrink-0 ${ui.footerBar}`}>
        <button
          type="button"
          onClick={submit}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white"
          style={{ backgroundColor: color }}
        >
          <Check size={20} /> Submit
        </button>
      </div>
    </div>
  );
}

export function NeoListView({ actionId, color, onBack, isDark = true }) {
  const ui = neoUi(isDark);
  const def = getActionDef(actionId);
  const [selected, setSelected] = useState(null);

  if (!def || def.type !== 'list') return null;

  if (selected) {
    return (
      <NeoDetailView
        item={selected}
        color={color}
        onBack={() => setSelected(null)}
        listActions={def.actions}
        isDark={isDark}
      />
    );
  }

  return (
    <div className={`flex flex-col h-full min-h-0 ${ui.page}`}>
      <header className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold tracking-wide truncate ${ui.title}`}>{def.title}</h2>
        <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'text-white/40 bg-white/5' : 'text-black/45 bg-black/5'}`}>{def.items?.length ?? 0}</span>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 pb-24">
        {(def.items || []).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelected(item)}
            className={`w-full flex items-start gap-3 p-4 rounded-2xl border text-left transition-colors ${ui.listRow}`}
          >
            <div className="p-2.5 rounded-[14px] border-[1.5px] shrink-0" style={{ borderColor: color, backgroundColor: `${color}22` }}>
              <ItemIcon name={item.icon} color={color} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${ui.title}`}>{item.title}</p>
              <p className={`text-[11px] truncate mt-0.5 ${ui.muted}`}>{item.subtitle}</p>
            </div>
            <div className="text-right shrink-0">
              <p className={`text-xs font-semibold ${isDark ? 'text-white/70' : 'text-black/65'}`}>{item.detail}</p>
              <span
                className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold"
                style={{ backgroundColor: `${item.statusColor}22`, color: item.statusColor }}
              >
                {item.status}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function NeoScannerView({ actionId, color, onBack, isDark = true }) {
  const ui = neoUi(isDark);
  const def = getActionDef(actionId);
  if (!def || def.type !== 'scanner') return null;
  const isQr = def.scanType === 'qr';

  return (
    <div className={`flex flex-col h-full min-h-0 ${ui.page}`}>
      <header className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold tracking-wide truncate pr-10 ${ui.title}`}>{def.title}</h2>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        <div className="relative w-56 h-56 border-2 rounded-2xl flex items-center justify-center" style={{ borderColor: color }}>
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 rounded-tl-xl" style={{ borderColor: color }} />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 rounded-tr-xl" style={{ borderColor: color }} />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 rounded-bl-xl" style={{ borderColor: color }} />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 rounded-br-xl" style={{ borderColor: color }} />
          {isQr ? <QrCode size={72} color={color} /> : <Barcode size={72} color={color} />}
        </div>
        <p className={`text-sm mt-8 text-center ${ui.muted}`}>
          Point camera at {isQr ? 'QR code' : 'barcode'}
        </p>
        <button
          type="button"
          onClick={() => window.alert('Scanned\nAsset: Commercial Refrigerator XL-5000\nID: AST-001')}
          className="mt-8 flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-white"
          style={{ backgroundColor: color }}
        >
          <ScanLine size={18} /> Simulate scan
        </button>
      </div>
    </div>
  );
}

export function NeoSuccessView({ message, onDone, isDark = true }) {
  const ui = neoUi(isDark);
  return (
    <div className={`flex flex-col items-center justify-center h-full min-h-0 px-8 ${ui.page}`}>
      <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
        <Check size={48} className="text-emerald-400" />
      </div>
      <h2 className={`text-2xl font-black mb-2 ${ui.title}`}>Success!</h2>
      <p className={`text-sm text-center mb-10 ${ui.muted}`}>{message}</p>
      <button
        type="button"
        onClick={onDone}
        className="px-10 py-4 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-500"
      >
        Continue
      </button>
    </div>
  );
}

export function NeoComingSoonView({ title, onBack, isDark = true }) {
  const ui = neoUi(isDark);
  return (
    <div className={`flex flex-col h-full min-h-0 ${ui.page}`}>
      <header className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${ui.headerBar}`}>
        <button type="button" onClick={onBack} className={`p-2 rounded-xl border ${ui.headerBtn}`}>
          <ChevronLeft size={18} />
        </button>
        <h2 className={`flex-1 text-center text-sm font-semibold truncate pr-10 ${ui.title}`}>Coming soon</h2>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        <Package size={56} className={isDark ? 'text-white/25 mb-4' : 'text-black/20 mb-4'} />
        <p className={`text-lg font-semibold text-center ${ui.title}`}>{title}</p>
        <p className={`text-sm text-center mt-2 ${ui.muted}`}>This module is under development in Lyncs Neo.</p>
      </div>
    </div>
  );
}

/** Renders the correct full-screen Neo drill-down for an action id */
export function NeoActionRouter({ actionId, color, onBack, isDark = true }) {
  const def = getActionDef(actionId);
  const [successMsg, setSuccessMsg] = useState('');

  const handleFormSubmit = useCallback(() => {
    const d = getActionDef(actionId);
    setSuccessMsg(`${d?.title || 'Request'} submitted.`);
  }, [actionId]);

  if (successMsg) {
    return (
      <NeoSuccessView
        message={successMsg}
        isDark={isDark}
        onDone={() => {
          setSuccessMsg('');
          onBack();
        }}
      />
    );
  }

  if (!def) {
    return <NeoComingSoonView title={String(actionId).replace(/-/g, ' ')} onBack={onBack} isDark={isDark} />;
  }

  if (def.type === 'form') {
    return <NeoFormView actionId={actionId} color={color} onBack={onBack} onSubmit={handleFormSubmit} isDark={isDark} />;
  }
  if (def.type === 'list') {
    return <NeoListView actionId={actionId} color={color} onBack={onBack} isDark={isDark} />;
  }
  if (def.type === 'scanner') {
    return <NeoScannerView actionId={actionId} color={color} onBack={onBack} isDark={isDark} />;
  }

  if (def.type === 'pulse-tab') {
    return <NeoPulseTabView actionId={actionId} color={color} onBack={onBack} isDark={isDark} />;
  }

  return <NeoComingSoonView title={actionId} onBack={onBack} isDark={isDark} />;
}
